<template>
  <div class="checkoutStep_cart">
    <app-spinner :rhombus-size="10" :color="'#9b9b9b'" v-if="loading" class="payment_spinner"/>
    <div class="ShoppingCartPayment-now" v-if="cartDetailsSummary.payNow">
      <div class="ShoppingCartPayment-now_item">
        <p>
          <span>{{ $t('shopping_cart.payment.subtotal', 'Subtotal') }}</span>
          <span>{{ cartDetailsSummary.payNow.original | formatCurrency }}</span>
        </p>
        <p v-if="cartDetailsSummary.payNow.discounts">
          <span>{{ $t('shopping_cart.payment.discount', 'Discount') }}</span>
          - {{ cartDetailsSummary.payNow.discounts | formatCurrency }}
        </p>
        <p class="deliver">
          <span>{{ $t('shopping_cart.payment.deliver.text', 'Delivery') }}</span>
          <span class="green">{{ $t('shopping_cart.payment.price.free', 'Free') }}</span>
        </p>
        <cart-estimated-tax
          v-if="(country === 'US'||country === 'CA') && cartDetailsSummary.payNow.hasExtraTax"
          :tax="cartDetailsSummary.payNow.tax"
          :bundles="bundles"
        />
      </div>
      <p class="ShoppingCartPayment-now_total">
        <span>{{ $t('shopping_cart.payment.total.price', 'Total') }}</span>
        <span>{{ (cartDetailsSummary.payNow.discounted + cartDetailsSummary.payNow.tax) | formatCurrency(true) }}</span>
      </p>
    </div>

    <div class="ShoppingCartPayment-deferred" v-if="!emptyRecurringPrice">
      <h4>
        {{ $t('shopping_cart.payment.deferred', 'Deferred payments') }}
      </h4>
      <!-- summary monthly charge -->
      <div class="ShoppingCartPayment-deferred_item" v-if="cartDetailsSummary.monthlyCharge">
        <div class="ShoppingCartPayment-deferred_item_header">
          <p>
            {{ $t('shopping_cart.payment.deferred.monthly', 'Monthly Payments') }}
          </p>
          <p class="text-right">
            <span class="discount" v-if="cartDetailsSummary.monthlyCharge.discounts">
              {{ (cartDetailsSummary.monthlyCharge.discounted) | formatCurrency(true) }}
            </span>
            <span class="original" :class="{'has-discount': !!cartDetailsSummary.monthlyCharge.discounts}">
              {{ (cartDetailsSummary.monthlyCharge.original) | formatCurrency }}
            </span>
          </p>
        </div>
        <div class="ShoppingCartPayment-deferred_item_body" v-show="showDeferredPaymentDetails">
          <div v-for="(item, index) in cartDetailsSummary.monthlyCharge.details" :key="index">
            <p>
              <span class="description">
                <span class="name">
                  {{ item.title }}
                </span>
                <span class="date">
                  {{ $t('shopping_cart.startingOn', 'starting on') }}
                  {{ item.monthlyCharge.beginDate | formatLocalDate }}
                </span>
              </span>
              <span class="price">
                {{ (item.monthlyCharge.original) | formatCurrency }}
              </span>
            </p>
            <p v-if="item.monthlyCharge.discounts">
              <span class="label">{{ $t('shopping_cart.payment.discount', 'Discount') }}</span>
              <span class="discount">- {{ item.monthlyCharge.discounts | formatCurrency }}</span>
            </p>
          </div>
        </div>
      </div>
      <!--  summary quarterly charge -->
      <div class="ShoppingCartPayment-deferred_item" v-if="cartDetailsSummary.quarterlyCharge">
        <div class="ShoppingCartPayment-deferred_item_header">
          <p>
            {{ $t('shopping_cart.payment.deferred.quarterly', 'Quarterly Payments') }}
          </p>
          <p class="text-right">
            <span class="discount" v-if="cartDetailsSummary.quarterlyCharge.discounts">
              {{ (cartDetailsSummary.quarterlyCharge.discounted) | formatCurrency(true) }}
            </span>
            <span class="original" :class="{'has-discount': !!cartDetailsSummary.quarterlyCharge.discounts}">
              {{ (cartDetailsSummary.quarterlyCharge.original) | formatCurrency }}
            </span>
          </p>
        </div>
        <div class="ShoppingCartPayment-deferred_item_body" v-show="showDeferredPaymentDetails">
          <div v-for="(item, index) in cartDetailsSummary.quarterlyCharge.details" :key="index">
            <p>
              <span class="description">
                <span class="name">
                  {{ item.title }}
                </span>
                <span class="date">
                  {{ $t('shopping_cart.startingOn', 'starting on') }}
                  {{ item.quarterlyCharge.beginDate | formatLocalDate }}
                </span>
              </span>
              <span class="price">
                {{ (item.quarterlyCharge.original) | formatCurrency }}
              </span>
            </p>
            <p v-if="item.quarterlyCharge.discounts">
              <span class="label">{{ $t('shopping_cart.payment.discount', 'Discount') }}</span>
              <span class="discount">- {{ item.quarterlyCharge.discounts | formatCurrency }}</span>
            </p>
          </div>
        </div>
      </div>
      <!-- summary semiAnnual charge -->
      <div class="ShoppingCartPayment-deferred_item" v-if="cartDetailsSummary.semiAnnualCharge">
        <div class="ShoppingCartPayment-deferred_item_header">
          <p>
            {{ $t('shopping_cart.payment.deferred.semiAnnual', 'SemiAnnual Payments') }}
          </p>
          <p class="text-right">
            <span class="discount" v-if="cartDetailsSummary.semiAnnualCharge.discounts">
              {{ (cartDetailsSummary.semiAnnualCharge.discounted) | formatCurrency(true) }}
            </span>
            <span class="original" :class="{'has-discount': !!cartDetailsSummary.semiAnnualCharge.discounts}">
              {{ (cartDetailsSummary.semiAnnualCharge.original) | formatCurrency }}
            </span>
          </p>
        </div>
        <div class="ShoppingCartPayment-deferred_item_body" v-show="showDeferredPaymentDetails">
          <div v-for="(item, index) in cartDetailsSummary.semiAnnualCharge.details" :key="index">
            <p>
              <span class="description">
                <span class="name">
                  {{ item.title }}
                </span>
                <span class="date">
                  {{ $t('shopping_cart.startingOn', 'starting on') }}
                  {{ item.semiAnnualCharge.beginDate | formatLocalDate }}
                </span>
              </span>
              <span class="price">
                {{ (item.semiAnnualCharge.original) | formatCurrency }}
              </span>
            </p>
            <p v-if="item.semiAnnualCharge.discounts">
              <span class="label">{{ $t('shopping_cart.payment.discount', 'Discount') }}</span>
              <span class="discount">- {{ item.semiAnnualCharge.discounts | formatCurrency }}</span>
            </p>
          </div>
        </div>
      </div>
      <!-- summary specificMonths charge -->
      <div class="ShoppingCartPayment-deferred_item" v-for="(specificCharge, index) in cartDetailsSummary.specificMonthsCharges" :key="index">
        <div class="ShoppingCartPayment-deferred_item_header">
          <p>
            {{
              $t('shopping_cart.payment.deferred.specificMonths', {
                defaultValue: `${specificCharge.specificMonths} months Payments`,
                specificMonths: specificCharge.specificMonths
              })
            }}
          </p>
          <p class="text-right">
            <span class="discount" v-if="specificCharge.charge.discounts">
              {{ (specificCharge.charge.discounted) | formatCurrency(true) }}
            </span>
            <span class="original" :class="{'has-discount': !!specificCharge.charge.discounts}">
              {{ (specificCharge.charge.original) | formatCurrency }}
            </span>
          </p>
        </div>
        <div class="ShoppingCartPayment-deferred_item_body" v-show="showDeferredPaymentDetails">
          <div v-for="(item, index) in specificCharge.charge.details" :key="index">
            <p>
              <span class="description">
                <span class="name">
                  {{ item.title }}
                </span>
                <span class="date">
                  {{ $t('shopping_cart.startingOn', 'starting on') }}
                  {{ item.specificMonthsCharges[0].charge.beginDate | formatLocalDate }}
                </span>
              </span>
              <span class="price">
                {{ (item.specificMonthsCharges[0].charge.original) | formatCurrency }}
              </span>
            </p>
            <p v-if="item.specificMonthsCharges[0].charge.discounts">
              <span class="label">{{ $t('shopping_cart.payment.discount', 'Discount') }}</span>
              <span class="discount">- {{ item.specificMonthsCharges[0].charge.discounts | formatCurrency }}</span>
            </p>
          </div>
        </div>
      </div>

      <a
        href="##"
        class="btn-toggle"
        :class="{ 'expand': showDeferredPaymentDetails, 'toggle-externals': loading }"
        @click.prevent="toggleDeferredPaymentDetails"
      >
        <span v-show="!showDeferredPaymentDetails">
          {{ $t('shopping_cart.payment.deferred.showDetails', 'Show breakdown') }}
        </span>
        <span v-show="showDeferredPaymentDetails">
          {{ $t('shopping_cart.payment.deferred.hideDetails', 'Hide breakdown') }}
        </span>
        <app-icon name="chevron-thin-down" />
      </a>
    </div>
  </div>
</template>

<script>
  import CartEstimatedTax from 'components/CartEstimatedTax'
  import { detectedCulture } from '@/utilities/common/culture'
  export default {
    name: 'checkout-step-cart',

    props: {
      cartDetailsSummary: {
        type: Object,
        required: true
      },

      loading: {
        type: Boolean,
        default: false
      },

      bundles: {
        type: Array,
        default: null
      }
    },

    data () {
      return {
        showDeferredPaymentDetails: false
      }
    },

    computed: {
      country () {
        return detectedCulture.countryCode
      },

      emptyRecurringPrice () {
        return !this.cartDetailsSummary.monthlyCharge &&
          !this.cartDetailsSummary.quarterlyCharge &&
          !this.cartDetailsSummary.semiAnnualCharge &&
          this.cartDetailsSummary.specificMonthsCharges.length === 0
      }
    },

    methods: {
      toggleDeferredPaymentDetails () {
        this.showDeferredPaymentDetails = !this.showDeferredPaymentDetails
        if (this.showDeferredPaymentDetails) {
          this.trackConversion({
            name: 'interaction',
            description: 'link:show_breakdown'
          })
        } else {
          this.trackConversion({
            name: 'interaction',
            description: 'link:close_breakdown'
          })
        }
      }
    },

    components: {
      CartEstimatedTax
    }
  }
</script>

<style lang="scss">
.checkoutStep_cart {
  .payment_spinner {
    right: 0 !important;
    top: 0 !important;
    left: 0 !important;
    margin: .2rem auto;
  }
}
</style>
